import React, { useState } from "react";
import ProjectCardImg from "../../assets/img/common/project-card-img.png";
import { Link } from "react-router-dom";
import UserPlaceHolderImg from "../../assets/img/common/user-placeholder-img.png";

import { BsClipboard2Data, BsTrash } from "react-icons/bs";
import { MdOutlineModeEditOutline } from "react-icons/md";

import {
  addTimeToDate,
  isoToString,
  remainingTime,
} from "../../utils/dateTool";
import useNavigateToProfile from "../../hooks/useNavigateToProfile";
import { useMemo } from "react";
import { getChain } from "../../utils/currencies";
import ConfirmPopup from "./ConfirmPopup";
import useDiscardProject from "../../hooks/useDiscardProject";
import { projectStateColor } from "../../Constants/project";

const ProjectCard = ({ data, isMyProject, isSlide }) => {
  let {
    _id,
    state,
    publishedAt,
    project_duration,
    category,
    title,
    creator,
    funding_goal,
    minimum_offer,
    no_minimum_offer,
    amount_raised,
    abstract,
    token: currencyToken,
  } = data ?? {};

  const [showConfirmPopup, setShowConfirmPopup] = useState(false);

  const { isLoading: discardProjectLoading, handleDiscardProject } =
    useDiscardProject({ onSuccess: () => setShowConfirmPopup(false) });

  // Use the navigateToProfile function from the custom hook
  const { navigateToProfile } = useNavigateToProfile();

  // Calculate progress value based on amount raised
  const progressValue = useMemo(() => {
    let value = 0;
    if (category === "Asian-Based Grant Initiative for DeSci" && amount_raised > 0) {
      value = 100;
    } else {
      // Calculate percentage of amount raised to funding goal
      value = Math.fround((amount_raised * 100) / funding_goal);
    }
    // Handle NaN and limit the value to a maximum of 100
    if (isNaN(Number(value)) || state === "Draft") return 0;
    return Number(value.toFixed(2));
  }, [
    category,
    state,
    amount_raised,
    funding_goal,
  ]);

  // Calculate remaining time for the project
  const projectRemainingTime = useMemo(
    () =>
      remainingTime({
        date: addTimeToDate({
          date: publishedAt,
          milliseconds: project_duration,
        }),
      }),
    [publishedAt, project_duration]
  );

  return (
    <div
      className={` ${
        isSlide ? "w-80 max-h-[550px]" : "w-full"
      }   h-auto  p-3 rounded-3xl bg-[#EDEDED] flex flex-col  font-roboto hover:shadow-2xl transition-all duration-300 `}
    >
      {/* ///// project image ////// */}
      <Link
        to={`/projects/${_id}`}
        className={`${
          isSlide ? "w-full" : "w-full"
        }   min-h-[16rem] max-w-full rounded-2xl relative overflow-hidden group/card `}
      >
        <img
          src={abstract || ProjectCardImg}
          alt="ProjectCardImg"
          className="w-full h-[16rem] rounded-t-2xl object-cover"
        />
        <span
          style={{
            backgroundColor: projectStateColor[state],
          }}
          className="absolute left-0 top-4 rounded-r-2xl text-sm text-white px-2 py-1"
        >
          {state}
        </span>
        {isMyProject && (
          <div className="hidden group-hover/card:flex flex-col items-end gap-3 absolute right-2 top-4 z-10">
            <Link
              onClick={(e) => e.stopPropagation()}
              to={`/create-project?edit=${_id}`}
              className="flex items-center group/action"
            >
              <span className="hidden group-hover/action:flex ml-2 text-white grow-up-animation bg-[#8f54ed] py-1 pr-5 pl-2 rounded-md text-sm shadow-sm  border-white">
                Edit
              </span>
              <div className="w-7 h-7 flex justify-center items-center rounded-full bg-primary z-10 ">
                <MdOutlineModeEditOutline
                  color="#ffffff"
                  className=" text-base"
                />
              </div>
            </Link>
            {state === "Draft" && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  return setShowConfirmPopup(true);
                }}
                className="flex items-center group/action z-20"
              >
                <span className="hidden group-hover/action:flex ml-2 text-white grow-up-animation bg-[#8f54ed] py-1 pr-5 pl-2 rounded-md text-sm shadow-sm  border-white">
                  Delete
                </span>
                <div className="w-7 h-7 flex justify-center items-center rounded-full bg-primary z-10 ">
                  <BsTrash className="text-white text-base" />
                </div>
              </button>
            )}

            {state !== "Draft" && (
              <Link
                onClick={(e) => e.stopPropagation()}
                to={`/create-result?project-id=${_id}&new=true`}
                className="flex items-center group/action"
              >
                <span className="hidden group-hover/action:flex ml-2 text-white grow-up-animation bg-[#8f54ed] py-1 pr-5 pl-2 rounded-md text-sm shadow-sm  border-white">
                  Submit Result
                </span>
                <div className="w-7 h-7 flex justify-center items-center rounded-full bg-primary z-10 ">
                  <BsClipboard2Data className="text-base text-white" />
                </div>
              </Link>
            )}
          </div>
        )}
      </Link>
      {/* ///// project detail ////// */}

      <div
        className={`
      ${isSlide ? "w-full" : "w-full"}
      w-full h-[275px] flex flex-col px-2 justify-between pb-1 items-start `}
      >
        <Link
          to={isMyProject ? `/create-project?edit=${_id}` : `/projects/${_id}`}
          className="font-semibold mt-3 h-16 text-base text-left"
        >
          {title
            ? `${title?.slice(0, 60)} ${title?.length > 60 ? "..." : ""}`
            : "UNTITLED_PROJECT"}
        </Link>
        <div className="w-full flex flex-col pb-3 border-b border-custom-gray-500 ">
          {/* ///////// */}

          <div className="flex justify-between items-center ">
            <span className="text-xs ml-1">Deadline:</span>
            <span className="text-xs font-bold ml-1">
              {publishedAt
                ? isoToString(
                    addTimeToDate({
                      date: publishedAt,
                      milliseconds: project_duration,
                    })
                  )
                : "UNDEFINED"}
            </span>
          </div>
          <div className="flex justify-between items-center mt-2">
            <span className="text-xs ml-1">Min. Funding : </span>
            {no_minimum_offer ? (
              <span className="text-xs font-bold ml-1 text-green-600">No Minimum</span>
            ) : (
              <span className="text-xs font-bold ml-1">
                ${minimum_offer || 0}
              </span>
            )}
          </div>
        </div>

        {/* /////////// */}
        <div className="flex items-center ">
          <span
            onClick={() =>
              navigateToProfile(creator?._id, creator?.role, false)
            }
            className=" rounded-full overflow-hidden flex justify-center items-center cursor-pointer h-8 w-8"
          >
            <img
              src={creator?.images?.profile || UserPlaceHolderImg}
              alt={title}
              className="transition-all duration-300 hover:scale-110 w-full h-full"
            />
          </span>
          <span className="flex flex-col ml-2 justify-between items-start">
            <button
              onClick={() => navigateToProfile(creator?._id, creator?.role)}
              className="text-sm font-bold "
            >
              {creator?.fundername ??
                `${creator?.firstname} ${creator?.lastname}`}
            </button>
            <button
              onClick={() => navigateToProfile(creator?._id, creator?.role)}
              className=" text-[#797979] text-xs "
            >
              {" "}
              {creator?.title_rank || creator?.location || creator?.email}
            </button>
          </span>
        </div>

        <div className="w-full flex flex-col mt-3 ">
          <div className="w-full relative rounded-3xl mb-2 bg-custom-gray-500 h-1.5">
            <div
              style={{ width: `${Math.min(100, progressValue)}%` }}
              className="bg-light-primary absolute left-0 top-0 h-full rounded-3xl"
            ></div>
          </div>
          <div className="w-full flex items-center justify-between poppins-font">
            <div className="flex flex-col items-center">
              <span className="font-bold mt-1 text-sm">{progressValue}%</span>
              <span className="text-[10px]">Funded</span>
            </div>
            {category !== "Asian-Based Grant Initiative for DeSci" && (
              <div className="flex flex-col items-center">
                <span className="font-bold mt-1 text-sm">
                  ${funding_goal || 0}
                </span>
                <span className="text-[10px]">Goal</span>
              </div>
            )}
            {projectRemainingTime ? (
              <div className="flex flex-col items-center">
                <span className="font-bold mt-1 text-sm">
                  {Math.round(projectRemainingTime.value)}
                </span>
                <span className="text-[10px]">
                  {projectRemainingTime.unit}
                  {projectRemainingTime?.value > 1 && "s"} left
                </span>
              </div>
            ) : state === "Draft" ? null : (
              <span className="font-bold text-sm">Expired</span>
            )}
          </div>
        </div>
      </div>
      {showConfirmPopup && (
        <ConfirmPopup
          isDelete={true}
          setShow={setShowConfirmPopup}
          show={showConfirmPopup}
          title="Delete Project"
          description={() => (
            <span>
              Are you sure you want to delete{" "}
              <Link
                to={`/projects/${_id}`}
                target="_blank"
                className="font-bold text-primary underline"
              >
                {title}
              </Link>{" "}
              project?
            </span>
          )}
          confirmText="Delete"
          onConfirm={() => handleDiscardProject(_id)}
          loading={discardProjectLoading}
        />
      )}
    </div>
  );
};

export default ProjectCard;
